/**
 * Color utilities and enums for charts, visualizations, and data display.
 *
 * These colors support light/dark themes via CSS variables defined in
 * src/styles/tokens/semantics.css. The enum values serve as fallbacks
 * for SSR and initial render before CSS is loaded.
 *
 * For theme-aware colors in React components, use the hook:
 * - `useChartColors()` - Returns resolved colors for current theme
 *
 * For one-off color resolution:
 * - `getCSSColor(cssVar, fallback)` - Resolves a single CSS variable
 */
import { countBy, sortBy } from 'utils/array';

// ============================================
// CSS Variable Names
// ============================================

/** CSS variable names for trend indicator colors */
export const TREND_LINE_CSS_VARS = [
    '--trend-indicators-1',
    '--trend-indicators-2',
    '--trend-indicators-3',
    '--trend-indicators-4',
    '--trend-indicators-5',
    '--trend-indicators-6',
    '--trend-indicators-7',
    '--trend-indicators-8',
    '--trend-indicators-9',
    '--trend-indicators-10',
    '--trend-indicators-11',
    '--trend-indicators-12',
    '--trend-indicators-13',
    '--trend-indicators-14',
    '--trend-indicators-15',
    '--trend-indicators-16',
    '--trend-indicators-17',
    '--trend-indicators-18',
    '--trend-indicators-19',
    '--trend-indicators-20',
] as const;

/** CSS variable names for health range colors */
export const HEALTH_RANGE_CSS_VARS = {
    unspecified: '--health-range-unspecified',
    off: '--health-range-off',
    healthy: '--health-range-healthy',
    faultDeveloping: '--health-range-fault-developing',
    unhealthy: '--health-range-unhealthy',
} as const;

/** CSS variable names for activity line colors */
export const ACTIVITY_LINE_CSS_VARS = {
    Alarm: '--activity-line-alarm',
    Warning: '--activity-line-warning',
    Manual: '--activity-line-manual',
    Automatic: '--activity-line-automatic',
    MachineIQ: '--activity-line-machineiq',
    MultipleActivities: '--activity-line-multiple',
    CreationMarker: '--activity-line-creation-marker',
    CriticalAlarm: '--activity-line-critical-alarm',
} as const;

/** CSS variable names for analysis marker colors */
export const ANALYSIS_MARKER_CSS_VARS = {
    Ruler: '--analysis-marker-ruler',
    '1x': '--analysis-marker-1x',
    BPFO: '--analysis-marker-bpfo',
    BPFI: '--analysis-marker-bpfi',
    BSF: '--analysis-marker-bsf',
    FTF: '--analysis-marker-ftf',
    '2xLF': '--analysis-marker-2xlf',
    LF: '--analysis-marker-lf',
    PPF: '--analysis-marker-ppf',
    RBF: '--analysis-marker-rbf',
    SSPF: '--analysis-marker-sspf',
} as const;

/** CSS variable names for indicator threshold colors */
export const INDICATOR_THRESHOLD_CSS_VARS = {
    off: '--indicator-threshold-off',
    alarm: '--indicator-threshold-alarm',
    warning: '--indicator-threshold-warning',
    criticalAlarm: '--indicator-threshold-critical-alarm',
} as const;

// ============================================
// Static Fallback Values (for SSR/initial render)
// ============================================

/**
 * Fallback colors for trend line series in charts.
 * 20 distinct colors to differentiate multiple data series.
 * Use getTrendLineColorForIndex() for theme-aware colors.
 * Note: this list _must_ match the values of the colors defined in TREND_LINE_CSS_VARS,
 * or it will not pass workbench state serialization validation.
 */
export enum TrendLineColors {
    trendLine1 = '#0070c4', // bluecta-600 (rebrand)
    trendLine2 = '#ffc107',
    trendLine3 = '#d10d54',
    trendLine4 = '#6f3fc9',
    trendLine5 = '#1de9b6',
    trendLine6 = '#c750df',
    trendLine7 = '#009ce1',
    trendLine8 = '#48a748',
    trendLine9 = '#1740d9',
    trendLine10 = '#f29d01',
    trendLine11 = '#ceb900',
    trendLine12 = '#9c27b0',
    trendLine13 = '#00bcd4',
    trendLine14 = '#c2d035',
    trendLine15 = '#fe5918',
    trendLine16 = '#52def0',
    trendLine17 = '#ff80ab',
    trendLine18 = '#8bc34a',
    trendLine19 = '#ea80fc',
    trendLine20 = '#006f7d',
}

export const TrendLineColorsList = Object.values(TrendLineColors);

/**
 * Fallback colors for health range zones on charts.
 * Use useChartColors() for theme-aware colors.
 */
export enum HealthRangeColors {
    unspecified = '#a2a7b3', // grey-400
    off = '#273D5A', // blueGrey
    healthy = '#228910', // green-800 (rebrand)
    faultDeveloping = '#e99c3d', // orange-600 (rebrand)
    unhealthy = '#e12635', // red-700 (rebrand)
}

/**
 * Fallback colors for activity timeline events.
 * Use useChartColors() for theme-aware colors.
 */
export enum ActivityLineColors {
    Alarm = '#ff1537',
    Warning = '#f29d01',
    Manual = '#0f48cd',
    Automatic = '#6c7c97',
    MachineIQ = '#39116d',
    MultipleActivities = '#0193a0',
    CreationMarker = '#2d3c51',
    CriticalAlarm = '#d2001b',
}

/**
 * Fallback colors for bearing analysis frequency markers.
 * Use useChartColors() for theme-aware colors.
 */
export enum AnalysisMarkerColors {
    Ruler = '#9298a6',
    '1x' = '#9c27b0',
    BPFO = '#fe5918',
    BPFI = '#0070c4',
    BSF = '#48a748',
    FTF = '#f29d01',
    '2xLF' = '#009ce1',
    LF = '#228910',
    PPF = '#d10d54',
    RBF = '#00bcd4',
    SSPF = '#ce4c00',
}

/**
 * Fallback colors for indicator threshold lines on charts.
 * Use useChartColors() for theme-aware colors.
 */
export enum IndicatorThresholdColors {
    off = '#a2a7b3', // grey-400
    alarm = '#ff1537', // red for alarm
    warning = '#f29d01', // orange for warning
    criticalAlarm = '#d2001b', // dark red for critical
}

// ============================================
// CSS Variable Resolution Utilities
// ============================================

/**
 * Resolves a CSS variable to its computed value.
 * Returns the fallback if running in SSR or if the variable is not defined.
 *
 * @param cssVar The CSS variable name (e.g., '--health-range-healthy')
 * @param fallback The fallback value if resolution fails
 */
export function getCSSColor(cssVar: string, fallback: string): string {
    if (typeof window === 'undefined') {
        return fallback;
    }

    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(cssVar)
        .trim();
    return value || fallback;
}

/**
 * Resolves all trend line colors from CSS variables.
 * Returns theme-aware colors for the current color mode.
 */
export function getTrendLineColorsFromCSS(): string[] {
    return TREND_LINE_CSS_VARS.map((cssVar, index) =>
        getCSSColor(cssVar, TrendLineColorsList[index])
    );
}

/**
 * Resolves all health range colors from CSS variables.
 * Returns theme-aware colors for the current color mode.
 */
export function getHealthRangeColorsFromCSS(): typeof HealthRangeColors {
    return {
        unspecified: getCSSColor(
            HEALTH_RANGE_CSS_VARS.unspecified,
            HealthRangeColors.unspecified
        ),
        off: getCSSColor(HEALTH_RANGE_CSS_VARS.off, HealthRangeColors.off),
        healthy: getCSSColor(HEALTH_RANGE_CSS_VARS.healthy, HealthRangeColors.healthy),
        faultDeveloping: getCSSColor(
            HEALTH_RANGE_CSS_VARS.faultDeveloping,
            HealthRangeColors.faultDeveloping
        ),
        unhealthy: getCSSColor(
            HEALTH_RANGE_CSS_VARS.unhealthy,
            HealthRangeColors.unhealthy
        ),
    } as typeof HealthRangeColors;
}

/**
 * Resolves all activity line colors from CSS variables.
 * Returns theme-aware colors for the current color mode.
 */
export function getActivityLineColorsFromCSS(): typeof ActivityLineColors {
    return {
        Alarm: getCSSColor(ACTIVITY_LINE_CSS_VARS.Alarm, ActivityLineColors.Alarm),
        Warning: getCSSColor(ACTIVITY_LINE_CSS_VARS.Warning, ActivityLineColors.Warning),
        Manual: getCSSColor(ACTIVITY_LINE_CSS_VARS.Manual, ActivityLineColors.Manual),
        Automatic: getCSSColor(
            ACTIVITY_LINE_CSS_VARS.Automatic,
            ActivityLineColors.Automatic
        ),
        MachineIQ: getCSSColor(
            ACTIVITY_LINE_CSS_VARS.MachineIQ,
            ActivityLineColors.MachineIQ
        ),
        MultipleActivities: getCSSColor(
            ACTIVITY_LINE_CSS_VARS.MultipleActivities,
            ActivityLineColors.MultipleActivities
        ),
        CreationMarker: getCSSColor(
            ACTIVITY_LINE_CSS_VARS.CreationMarker,
            ActivityLineColors.CreationMarker
        ),
        CriticalAlarm: getCSSColor(
            ACTIVITY_LINE_CSS_VARS.CriticalAlarm,
            ActivityLineColors.CriticalAlarm
        ),
    } as typeof ActivityLineColors;
}

/**
 * Resolves all analysis marker colors from CSS variables.
 * Returns theme-aware colors for the current color mode.
 */
export function getAnalysisMarkerColorsFromCSS(): typeof AnalysisMarkerColors {
    return {
        Ruler: getCSSColor(ANALYSIS_MARKER_CSS_VARS.Ruler, AnalysisMarkerColors.Ruler),
        '1x': getCSSColor(ANALYSIS_MARKER_CSS_VARS['1x'], AnalysisMarkerColors['1x']),
        BPFO: getCSSColor(ANALYSIS_MARKER_CSS_VARS.BPFO, AnalysisMarkerColors.BPFO),
        BPFI: getCSSColor(ANALYSIS_MARKER_CSS_VARS.BPFI, AnalysisMarkerColors.BPFI),
        BSF: getCSSColor(ANALYSIS_MARKER_CSS_VARS.BSF, AnalysisMarkerColors.BSF),
        FTF: getCSSColor(ANALYSIS_MARKER_CSS_VARS.FTF, AnalysisMarkerColors.FTF),
        '2xLF': getCSSColor(
            ANALYSIS_MARKER_CSS_VARS['2xLF'],
            AnalysisMarkerColors['2xLF']
        ),
        LF: getCSSColor(ANALYSIS_MARKER_CSS_VARS.LF, AnalysisMarkerColors.LF),
        PPF: getCSSColor(ANALYSIS_MARKER_CSS_VARS.PPF, AnalysisMarkerColors.PPF),
        RBF: getCSSColor(ANALYSIS_MARKER_CSS_VARS.RBF, AnalysisMarkerColors.RBF),
        SSPF: getCSSColor(ANALYSIS_MARKER_CSS_VARS.SSPF, AnalysisMarkerColors.SSPF),
    } as typeof AnalysisMarkerColors;
}

/**
 * Resolves all indicator threshold colors from CSS variables.
 * Returns theme-aware colors for the current color mode.
 */
export function getIndicatorThresholdColorsFromCSS(): typeof IndicatorThresholdColors {
    return {
        off: getCSSColor(INDICATOR_THRESHOLD_CSS_VARS.off, IndicatorThresholdColors.off),
        alarm: getCSSColor(
            INDICATOR_THRESHOLD_CSS_VARS.alarm,
            IndicatorThresholdColors.alarm
        ),
        warning: getCSSColor(
            INDICATOR_THRESHOLD_CSS_VARS.warning,
            IndicatorThresholdColors.warning
        ),
        criticalAlarm: getCSSColor(
            INDICATOR_THRESHOLD_CSS_VARS.criticalAlarm,
            IndicatorThresholdColors.criticalAlarm
        ),
    } as typeof IndicatorThresholdColors;
}

// ============================================
// Legacy Utility Functions (with theme support)
// ============================================

/**
 * Gets a distinct color for a series based on its index.
 * Cycles through the 20 trend line colors.
 * Resolves CSS variables for theme-aware colors.
 *
 * @param index The index of the series
 */
export function getTrendLineColorForIndex(index: number): string {
    const cssVar = TREND_LINE_CSS_VARS[index % TREND_LINE_CSS_VARS.length];
    const fallback = TrendLineColorsList[index % TrendLineColorsList.length];
    return getCSSColor(cssVar, fallback);
}

/**
 * Calculates the next trend color based on the current selection colors.
 * The next trend color is either the color that has been used the least
 * or has the smallest positional index in the list of trend colors.
 * Resolves CSS variables for theme-aware colors.
 *
 * @param currentColors The current color selections.
 */
export function getNextTrendLineColor(currentColors: string[]): string {
    const resolvedColors = getTrendLineColorsFromCSS();

    if (currentColors.length === 0) {
        return resolvedColors[0];
    }

    const colorCounts = resolvedColors.map((color, index) => ({
        color,
        index,
        count: countBy(currentColors, (current) => current === color),
    }));

    const sortedCounts = sortBy(
        colorCounts,
        (count) => count.count,
        (count) => count.index
    );

    return sortedCounts[0].color;
}
